'use client'

import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { useFocusTrap } from './useFocusTrap'

export type ModalSize = 'sm' | 'md' | 'lg'
export type ModalPlacement = 'center' | 'bottom-sheet'

export type ModalShellProps = {
  open: boolean
  onClose: () => void
  /** aria-label for the dialog — use when there is no visible heading to point at. */
  label?: string
  /** id of the visible heading — prefer this over `label` when the card has an `<h2 id>`. */
  labelledById?: string
  /** Card max-width. Defaults to `md`. */
  size?: ModalSize
  /** `bottom-sheet` (mobile sheet → desktop centered) or fully `center`. Defaults to `bottom-sheet`. */
  placement?: ModalPlacement
  /** When true, Escape and backdrop-click do NOT close (e.g. a submit in flight). */
  busy?: boolean
  /** Master switch — false removes backdrop-close + Escape entirely (rare). Defaults to true. */
  dismissible?: boolean
  /** Render a floating ✕ in the top-right of the card. Defaults to false. */
  showClose?: boolean
  /** Extra classes appended to the card (e.g. a wider size or custom surface). */
  className?: string
  /** Backdrop tint. Defaults to the calm `bg-black/40`. */
  backdropClassName?: string
  children: ReactNode
}

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
}

/**
 * ModalShell — the one storefront modal primitive: fixed backdrop, centered (or
 * mobile bottom-sheet) card, `role="dialog"`/`aria-modal`, Escape + backdrop
 * dismissal, focus trap + invoker restore (via `useFocusTrap`), and body
 * scroll-lock while open. Consumers pass only the card's inner content.
 *
 * Seeded from PopupShell's modal branch and the hand-rolled account modals
 * (ChangeEmailModal / CopyToOtherDealersModal / AddSerialSheet), which each
 * re-implemented this scaffolding. `busy` gates dismissal so a modal can't be
 * closed mid-submit; pass the caller's own compound condition
 * (`pending || uploading`, etc.).
 */
export function ModalShell({
  open,
  onClose,
  label,
  labelledById,
  size = 'md',
  placement = 'bottom-sheet',
  busy = false,
  dismissible = true,
  showClose = false,
  className,
  backdropClassName = 'bg-black/40',
  children,
}: ModalShellProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const canClose = dismissible && !busy
  const requestClose = () => {
    if (canClose) onClose()
  }

  useFocusTrap({
    active: open,
    containerRef: cardRef,
    onEscape: canClose ? onClose : undefined,
  })

  // Lock body scroll while open; restore the prior value on close/unmount.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (!open) return null

  const wrapperClass =
    placement === 'center'
      ? 'items-center justify-center p-4'
      : 'items-end justify-center sm:items-center'
  const roundingClass =
    placement === 'center' ? 'rounded-2xl' : 'rounded-t-2xl sm:rounded-2xl'

  return (
    <div className={`fixed inset-0 z-50 flex ${wrapperClass}`} role="presentation">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={requestClose}
        disabled={!canClose}
        className={`absolute inset-0 ${backdropClassName}`}
      />
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={labelledById ? undefined : label}
        aria-labelledby={labelledById}
        className={`relative w-full ${SIZE[size]} ${roundingClass} max-h-[90vh] overflow-auto bg-[var(--color-bg-primary)] p-6 shadow-xl${className ? ` ${className}` : ''}`}
      >
        {showClose && canClose && (
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="absolute right-3 top-3 inline-flex items-center justify-center rounded p-1 text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        {children}
      </div>
    </div>
  )
}
